import { useEffect, useRef, useState } from "react";

/* Simple lazy image with fade-in on load. */
export function LazyImage({ src, alt, className = "", aspect = "aspect-[4/5]", priority = false }) {
    const [loaded, setLoaded] = useState(false);
    const [inView, setInView] = useState(priority);
    const ref = useRef(null);

    useEffect(() => {
        if (priority) return;
        const el = ref.current;
        if (!el || !("IntersectionObserver" in window)) {
            setInView(true);
            return;
        }
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        setInView(true);
                        obs.disconnect();
                    }
                });
            },
            { rootMargin: "200px 0px" }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [priority]);

    return (
        <div
            ref={ref}
            className={`relative overflow-hidden bg-champagne ${aspect} ${className}`}
        >
            {inView && (
                <img
                    src={src}
                    alt={alt}
                    loading={priority ? "eager" : "lazy"}
                    decoding="async"
                    onLoad={() => setLoaded(true)}
                    className={`lazy-img absolute inset-0 h-full w-full object-cover ${loaded ? "loaded" : ""}`}
                />
            )}
            {!loaded && (
                <div
                    className="absolute inset-0 animate-shimmer"
                    style={{
                        backgroundImage:
                            "linear-gradient(90deg, #F3E9DC 0%, #FAF7F2 50%, #F3E9DC 100%)",
                        backgroundSize: "200% 100%",
                    }}
                />
            )}
        </div>
    );
}

/* IntersectionObserver-powered scroll reveal wrapper. */
export function Reveal({ children, className = "", as: Tag = "div", delay = 0 }) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        setTimeout(() => el.classList.add("is-visible"), delay);
                        obs.disconnect();
                    }
                });
            },
            { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [delay]);
    return (
        <Tag ref={ref} className={`reveal ${className}`}>
            {children}
        </Tag>
    );
}
